import React, { useCallback, useEffect, useState } from "react";

import { AxiosResponse } from "axios"
import { toast } from "react-toastify"
import { useMutation } from "react-query"

import { checkFileSize, compressFile } from "@/utils/CommonFunctions";
import { UPLOAD_IMAGE_SIZE } from "@/utils/constants/globalVariables";
import { apiClient } from "@/apiClient/apiService";
import API_END_POINT, { ROUTES } from "@/apiClient/apiEndpoints";


interface UploadInput {
    name: string
    file: File
}

interface UploadResponse {
    image_url: string;
    uploaded_at?: EpochTimeStamp;
}

interface APIError {
    response?: {
        data?: {
            message: string
            uploaded_at?: EpochTimeStamp
        }
    }
}

type Props = {
    maxFileSize?: number
    maxNumOfImages?: number
    toastsEnabled?: boolean
    numOfAlreadyUploaded?: number

    onError?: (error: Error) => void
    onSuccess?: (uploadResponse: UploadResponse) => void
}

export function useImageFileUpload({ toastsEnabled, maxFileSize = UPLOAD_IMAGE_SIZE, maxNumOfImages = 1, numOfAlreadyUploaded = 0, onError, onSuccess }: Props) {

    const [isCompressing, setIsCompressing] = useState(false)
    const [uploadQueue, setUploadQueue] = useState<File[]>([])
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

    const validateFileWithSize = checkFileSize(maxFileSize)

    const validateIfSmallFile = checkFileSize(0.6 * maxFileSize)

    const uploadImage = async (data: UploadInput): Promise<UploadResponse> => {
        const formData = new FormData()
        formData.append("name", data.name)
        formData.append("image", data.file)

        const response: AxiosResponse<UploadResponse> = await apiClient.post(`${API_END_POINT.POST[ROUTES.uploadImages]}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })

        return response.data
    }

    const { mutate: uploadImageMutation, isLoading } = useMutation<UploadResponse, APIError, UploadInput>({
        mutationFn: uploadImage,
        onSuccess: (response: UploadResponse) => {
            setUploadQueue((prevQueue) => prevQueue.slice(1))
            onSuccess?.(response)
            setErrorMessage(undefined)
        },
        onError: (error: APIError) => {
            const errorMsg = error?.response?.data?.message || "Error uploading the image"
            setUploadQueue([])
            toastsEnabled ? toast.error(errorMessage) : setErrorMessage(errorMsg)
            onError?.(new Error(errorMsg))
        }
    })

    const fileUploadHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target
        if (files?.length) {
            const selectedFiles = Array.from(files)
            if (selectedFiles.length + numOfAlreadyUploaded > maxNumOfImages) {
                const errMsg = `You can only add up to ${maxNumOfImages} images`
                toastsEnabled ? toast.error(errMsg) : setErrorMessage(errMsg)
                return
            }
            const compressedImages: File[] = []
            setErrorMessage(undefined)
            setIsCompressing(true)
            Promise.all(selectedFiles.map((file) => {
                return new Promise((res) => {
                    const isAlreadySmallFile = validateIfSmallFile(file)
                    if (isAlreadySmallFile) {
                        compressedImages.push(file)
                        res(file)
                    } else {
                        compressFile(file).then(result => {
                            compressedImages.push(result);
                            res(result)
                        })
                    }
                })
            })).then(() => {
                const isValidFileSize = validateFileWithSize(compressedImages)
                setIsCompressing(false)
                if (!isValidFileSize) {
                    const errMsg = `One or more images exceeds the ${maxFileSize / 1024} MB size limit`
                    toastsEnabled ? toast.error(errMsg) : setErrorMessage(errMsg)
                    return
                }
                setUploadQueue(prev => [...prev, ...compressedImages])
            }).catch(() => {
                setIsCompressing(false)
                const errMsg = "Error while compressing images!"
                toastsEnabled ? toast.error(errMsg) : setErrorMessage(errMsg)
            })
        } else {
            const errMsg = 'Please select a valid image!'
            toastsEnabled ? toast.error(errMsg) : setErrorMessage(errMsg)
        }
    }, [maxFileSize, maxNumOfImages, numOfAlreadyUploaded, toastsEnabled, validateFileWithSize, validateIfSmallFile])

    const processUploadQueue = useCallback(() => {
        if (uploadQueue.length > 0 && !isLoading) {
            const file = uploadQueue[0]
            const uploadInput: UploadInput = { name: file.name, file }
            uploadImageMutation(uploadInput)
        }
    }, [uploadQueue, isLoading, uploadImageMutation])

    useEffect(() => {
        processUploadQueue()
    }, [processUploadQueue])

    const clearErrorMessages = useCallback(() => {
        setErrorMessage(undefined)
    }, [])

    return {
        errorMessage,
        isLoading: isLoading || isCompressing,

        fileUploadHandler,
        clearErrorMessages
    }
}


